import { apiClient, apiMutate } from "~/utils/apiClient";

const { VITE_CONVERSATIONS_API } = import.meta.env;

export const SkillScope = {
  SYSTEM: "SYSTEM",
  USER: "USER",
  AGENT: "AGENT",
  DATASET: "DATASET",
} as const;
export type SkillScope = (typeof SkillScope)[keyof typeof SkillScope];

export interface PartialSkill {
  id: string;
  name: string;
  description: string;
  scope: SkillScope;
  scope_id: string | null;
  enabled: boolean;
  created_by: string | null;
  updated_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface Skill extends PartialSkill {
  content: string;
}

export interface SkillPageMetadata {
  limit: number;
  offset: number;
  total_count: number;
  has_more: boolean;
}

export interface SkillPage<T = PartialSkill> {
  items: T[];
  metadata: SkillPageMetadata;
}

export interface SkillFields {
  name: string;
  description: string;
  content: string;
}

export interface SkillUpdateInput {
  name?: string;
  description?: string;
  content?: string;
  enabled?: boolean;
}

export const SKILL_NAME_MAX_LENGTH = 255;
export const SKILL_NAME_MIN_LENGTH = 3;
export const SKILL_DESCRIPTION_MAX_LENGTH = 2048;
export const SKILL_CONTENT_MAX_LENGTH = 100000;
export const SKILL_NAME_PATTERN = /^[a-z][a-z0-9-]*$/;

export const DEFAULT_SKILL_CONTENT = `# my-skill

Describe when this skill applies and the steps the agent should follow.

## When to use it

- Trigger condition 1
- Trigger condition 2

## How to apply

1. **First step**: …
2. **Second step**: …
3. **Third step**: …
`;

export const skillsListPath = (params: Record<string, string | number | boolean>) => {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)]))
  );
  return `${VITE_CONVERSATIONS_API}/skills/?${qs.toString()}`;
};

export const skillPath = (skillId: string) => `${VITE_CONVERSATIONS_API}/skills/${skillId}`;

export const agentSkillsListPath = (
  agentId: string,
  params: Record<string, string | number | boolean> = {}
) => {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)]))
  );
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return `${VITE_CONVERSATIONS_API}/agents/${agentId}/skills${suffix}`;
};

export const agentSkillPath = (agentId: string, skillId: string) =>
  `${VITE_CONVERSATIONS_API}/agents/${agentId}/skills/${skillId}`;

export async function getSkill(skillId: string): Promise<Skill> {
  return apiClient<Skill>(skillPath(skillId));
}

export async function updateSkill(skillId: string, body: SkillUpdateInput): Promise<Skill> {
  return apiMutate<Skill>(skillPath(skillId), "PATCH", body);
}

export async function attachAgentSkillFromFields(
  agentId: string,
  fields: SkillFields
): Promise<Skill> {
  return apiMutate<Skill>(agentSkillsListPath(agentId), "POST", { fields });
}

export async function detachAgentSkill(agentId: string, skillId: string): Promise<void> {
  await apiMutate(agentSkillPath(agentId, skillId), "DELETE", undefined, { expects204: true });
}
